package com.rainbowclubs.rainbow.presentation.more

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.domain.use_cases.login.LogOutUseCase
import com.rainbowclubs.rainbow.domain.use_cases.user.GetUserUseCase
import com.rainbowclubs.rainbow.presentation.ScreenState
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

data class MoreUiState(
    val userName: String,
    val userId: String,
)

class MoreViewModel(
    private val clubId: String,
    private val getUserUseCase: GetUserUseCase,
    private val logOutUseCase: LogOutUseCase,
) : ViewModel() {

    sealed class MoreNavigationEvent {
        data class Home(val clubId: String) : MoreNavigationEvent()
        data class Club(val clubId: String) : MoreNavigationEvent()
        data class Messages(val clubId: String) : MoreNavigationEvent()
        object SelectClub : MoreNavigationEvent()
        object Landing : MoreNavigationEvent()
    }

    private val _screenState = MutableStateFlow<ScreenState<MoreUiState>>(ScreenState.Loading)
    val screenState: StateFlow<ScreenState<MoreUiState>> = _screenState.asStateFlow()

    private val navigationIntentChannel = Channel<MoreNavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<MoreNavigationEvent> = navigationIntentChannel.receiveAsFlow()

    init {
        fetchUser()
    }

    fun fetchUser() {
        val user = getUserUseCase()
        val userId = user?.id.orEmpty()
        if (user == null || userId.isBlank()) {
            _screenState.value = ScreenState.Error(IllegalStateException("User is not available"))
            return
        }

        _screenState.value = ScreenState.Content(
            MoreUiState(
                userName = user.name?.takeIf { it.isNotBlank() }
                    ?: user.email?.takeIf { it.isNotBlank() }
                    ?: "Unknown",
                userId = userId,
            )
        )
    }

    fun navigateToHome() {
        navigationIntentChannel.trySend(MoreNavigationEvent.Home(clubId))
    }

    fun navigateToClub() {
        navigationIntentChannel.trySend(MoreNavigationEvent.Club(clubId))
    }

    fun navigateToMessages() {
        navigationIntentChannel.trySend(MoreNavigationEvent.Messages(clubId))
    }

    fun navigateToSelectClub() {
        navigationIntentChannel.trySend(MoreNavigationEvent.SelectClub)
    }

    fun logOut() {
        viewModelScope.launch {
            logOutUseCase().onSuccess {
                navigationIntentChannel.trySend(MoreNavigationEvent.Landing)
            }
        }
    }
}
